import React, { useEffect, useRef } from 'react';
import { View, StyleSheet, Platform } from 'react-native';
import { RD } from '../theme/colors';
import { loadGoogleMapsScript } from '../utils/googleMaps';

interface Props {
  latitude: number;
  longitude: number;
  radiusMiles: number;
  height?: number;
}

// Convert miles to meters for Google Maps circle
function milesToMeters(miles: number): number {
  return miles * 1609.34;
}

// Web implementation using Google Maps JS API
function RadiusMapWeb({ latitude, longitude, radiusMiles, height = 260 }: Props) {
  const mapRef = useRef<HTMLDivElement>(null);
  const mapInstanceRef = useRef<any>(null);
  const circleRef = useRef<any>(null);
  const markerRef = useRef<any>(null);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    loadGoogleMapsScript(() => {
      if (!mapRef.current) return;
      const google = (window as any).google;

      if (!mapInstanceRef.current) {
        mapInstanceRef.current = new google.maps.Map(mapRef.current, {
          center: { lat: latitude, lng: longitude },
          zoom: getZoomForRadius(radiusMiles),
          disableDefaultUI: true,
          zoomControl: true,
          styles: mapStyles,
        });

        markerRef.current = new google.maps.Marker({
          position: { lat: latitude, lng: longitude },
          map: mapInstanceRef.current,
          icon: {
            path: google.maps.SymbolPath.CIRCLE,
            scale: 10,
            fillColor: RD.maroon,
            fillOpacity: 1,
            strokeColor: '#fff',
            strokeWeight: 3,
          },
        });

        circleRef.current = new google.maps.Circle({
          map: mapInstanceRef.current,
          center: { lat: latitude, lng: longitude },
          radius: milesToMeters(radiusMiles),
          fillColor: RD.info,
          fillOpacity: 0.12,
          strokeColor: RD.info,
          strokeOpacity: 0.4,
          strokeWeight: 2,
        });
      } else {
        const center = { lat: latitude, lng: longitude };
        mapInstanceRef.current.setCenter(center);
        mapInstanceRef.current.setZoom(getZoomForRadius(radiusMiles));
        markerRef.current.setPosition(center);
        circleRef.current.setCenter(center);
        circleRef.current.setRadius(milesToMeters(radiusMiles));
      }
    });
  }, [latitude, longitude, radiusMiles]);

  return (
    <View style={[styles.container, { height }]}>
      <div ref={mapRef} style={{ width: '100%', height: '100%', borderRadius: 16 }} />
    </View>
  );
}

// Native implementation using react-native-maps
function RadiusMapNative({ latitude, longitude, radiusMiles, height = 260 }: Props) {
  const MapView = require('react-native-maps').default;
  const { Circle, Marker } = require('react-native-maps');

  return (
    <View style={[styles.container, { height }]}>
      <MapView
        style={StyleSheet.absoluteFillObject}
        region={{
          latitude,
          longitude,
          latitudeDelta: radiusMiles * 0.03,
          longitudeDelta: radiusMiles * 0.03,
        }}
        scrollEnabled={false}
        zoomEnabled={false}
      >
        <Marker coordinate={{ latitude, longitude }} />
        <Circle
          center={{ latitude, longitude }}
          radius={milesToMeters(radiusMiles)}
          fillColor="rgba(46, 111, 184, 0.12)"
          strokeColor="rgba(46, 111, 184, 0.4)"
          strokeWidth={2}
        />
      </MapView>
    </View>
  );
}

export function RadiusMap(props: Props) {
  if (Platform.OS === 'web') {
    return <RadiusMapWeb {...props} />;
  }
  return <RadiusMapNative {...props} />;
}

// ─── Helpers ────────────────────────────────────────────────────────────────

function getZoomForRadius(miles: number): number {
  if (miles <= 20) return 9;
  if (miles <= 50) return 8;
  if (miles <= 100) return 7;
  if (miles <= 200) return 6;
  if (miles <= 500) return 5;
  return 4;
}

const mapStyles = [
  { featureType: 'poi', stylers: [{ visibility: 'off' }] },
  { featureType: 'transit', stylers: [{ visibility: 'off' }] },
  { featureType: 'water', stylers: [{ color: '#d4e4f7' }] },
  { featureType: 'landscape', stylers: [{ color: '#f5f0eb' }] },
];

const styles = StyleSheet.create({
  container: {
    borderRadius: 16,
    overflow: 'hidden',
    backgroundColor: RD.g07,
  },
});
